import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Button,
  Space,
  message,
  Tooltip,
  Tag,
  Popconfirm,
  Modal,
  Input,
  Checkbox as AntCheckbox,
} from 'antd';
import {
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  SearchOutlined,
  ColumnHeightOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { customerService, Customer } from '../services/customers';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { useListingTableFocus } from '../hooks/useListingTableFocus';
import { useListingColumnsPersistence } from '../hooks/useListingColumnsPersistence';
import { useListingColumnWidthsPersistence } from '../hooks/useListingColumnWidthsPersistence';
import { useListingSortPersistence } from '../hooks/useListingSortPersistence';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import api from '../services/api';
import '../styles/resizable-table.css';
import dayjs from 'dayjs';
import {
  Table as FlowbiteTable,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Alert,
} from 'flowbite-react';
import { Resizable } from 'react-resizable';

const SignatureThumb: React.FC<{ customerId: number; signature?: string | null }> = ({ customerId, signature }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let blobUrlToRevoke: string | null = null;

    if (!signature || !customerId) {
      setPreviewUrl(null);
      return () => {
        if (blobUrlToRevoke) {
          URL.revokeObjectURL(blobUrlToRevoke);
        }
      };
    }

    const loadPreview = async () => {
      try {
        const response = await api.get(`/customers/${customerId}/supporting-files/signature/view`, {
          responseType: 'blob',
        });
        if (cancelled) return;
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'image/png',
        });
        const blobUrl = URL.createObjectURL(blob);
        blobUrlToRevoke = blobUrl;
        setPreviewUrl(blobUrl);
      } catch {
        if (!cancelled) {
          setPreviewUrl(null);
        }
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
      if (blobUrlToRevoke) {
        URL.revokeObjectURL(blobUrlToRevoke);
      }
      setPreviewUrl((currentUrl) => {
        if (currentUrl && currentUrl.startsWith('blob:')) {
          URL.revokeObjectURL(currentUrl);
        }
        return null;
      });
    };
  }, [customerId, signature]);

  if (!signature) {
    return <span>-</span>;
  }

  if (!previewUrl) {
    return <span>Loading...</span>;
  }

  return (
    <img
      src={previewUrl}
      alt="Signature"
      style={{ width: 36, height: 24, objectFit: 'contain', display: 'block' }}
    />
  );
};

type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

type FilterType = 'all' | 'potential' | 'active' | 'inactive';
type SortOrder = 'ascend' | 'descend' | null;

const formatDateValue = (date?: string) => (date ? dayjs(date).format('DD/MM/YYYY') : '');

interface FlowbiteResizableHeaderProps {
  width?: number | string;
  maxWidth?: number;
  onResize?: (e: any, data: { size: { width: number } }) => void;
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
}

const FlowbiteResizableHeader: React.FC<FlowbiteResizableHeaderProps> = ({
  width,
  maxWidth,
  onResize,
  onClick,
  className,
  children,
}) => {
  if (typeof width !== 'number' || !onResize) {
    return (
      <TableHeadCell
        className={className}
        style={width ? { width } : undefined}
        onClick={onClick}
      >
        {children}
      </TableHeadCell>
    );
  }

  return (
    <Resizable
      width={width}
      height={0}
      onResize={(e, data) => {
        const newWidth = Math.max(data.size.width, 50);
        onResize(e, { ...data, size: { ...data.size, width: newWidth } });
      }}
      resizeHandles={['e']}
      handle={
        <span
          className="resizable-table-handle"
          style={{ height: '100%' }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      }
      draggableOpts={{ enableUserSelectHack: false }}
      maxConstraints={typeof maxWidth === 'number' ? [maxWidth, 0] : undefined}
      minConstraints={[50, 0]}
    >
      <TableHeadCell
        className={className ? `relative ${className}` : 'relative'}
        style={{ width }}
        onClick={onClick}
      >
        {children}
      </TableHeadCell>
    </Resizable>
  );
};


export const Customers: React.FC = () => {
  const listingKey = 'personal';
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [submitNotice, setSubmitNotice] = useState<{ type: 'success' | 'failure'; message: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [excludedRowKeys, setExcludedRowKeys] = useState<React.Key[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [columnWidthByKey, setColumnWidthByKey] = useState<Record<string, number>>({});
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [columnsSearch, setColumnsSearch] = useState('');
  const [pendingVisibleKeys, setPendingVisibleKeys] = useState<React.Key[]>([]);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [availableWidth, setAvailableWidth] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [statusCountsFromApi, setStatusCountsFromApi] = useState<{ all: number; potential: number; active: number; inactive: number } | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();

  useEffect(() => {
    const stateNotice = (location.state as { notice?: { type: 'success' | 'failure'; message: string } } | null)?.notice;
    if (stateNotice) {
      setSubmitNotice(stateNotice);
      navigate(location.pathname + location.search, { replace: true });
    }
  }, [location, navigate]);
  const { user } = useAuth();
  const canView = hasPermission(user, 'customer.personal.view');
  const canCreate = hasPermission(user, 'customer.personal.create');
  const canDelete = hasPermission(user, 'customer.personal.delete');
  const numericSearchKeys = useMemo(
    () =>
      new Set([
        'ic_passport_no',
        'tin_no',
        'contact_number',
        'installment',
        'spouse',
        'child',
      ]),
    []
  );
  const wordSearchKeys = useMemo(
    () =>
      new Set([
        'name',
        'nationality',
        'race',
        'sex',
        'type',
        'assessment',
        'email',
        'address1',
        'bank',
        'agent_staff_id',
        'agent_appoint_date',
        'agent_resign_date',
        'staff_id',
        'appoint_date',
        'resign_date',
        'lhdn_password',
        'signature',
        'directors',
        'members',
        'pending_requests',
      ]),
    []
  );
  const statusCounts = useMemo(() => {
    if (statusCountsFromApi) {
      return statusCountsFromApi;
    }
    const counts = { all: customers.length, potential: 0, active: 0, inactive: 0 };
    customers.forEach((customer) => {
      if (customer.type === 'potential') counts.potential += 1;
      if (customer.type === 'active') counts.active += 1;
      if (customer.type === 'inactive') counts.inactive += 1;
    });
    return counts;
  }, [customers, statusCountsFromApi]);

  useEffect(() => {
    if (!canView) {
      setCustomers([]);
      return;
    }
    fetchCustomers();
  }, [canView]);

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Personal
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const fetchCustomers = async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    try {
      const response = await customerService.getAll();
      const { items, total, statusCounts: apiStatusCounts } = normalizeCustomersResponse(response);
      setCustomers(items);
      const resolvedTotal = typeof total === 'number' && total > 0 ? total : items.length;
      setTotalCount(resolvedTotal);
      setStatusCountsFromApi(apiStatusCounts);
    } catch (error) {
      message.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const normalizeCustomersResponse = (response: any) => {
    const raw = response?.data ?? response;
    const meta = raw?.meta;
    const payload = raw?.data && !Array.isArray(raw?.data) ? raw.data : raw;
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];
    const total = Number(payload?.total ?? meta?.total ?? 0);
    const nextPageExists = false;
    const statusCountsMeta = meta?.status_counts;
    const statusCounts = statusCountsMeta
      ? {
          all: total || 0,
          potential: Number(statusCountsMeta.potential ?? 0),
          active: Number(statusCountsMeta.active ?? 0),
          inactive: Number(statusCountsMeta.inactive ?? 0),
        }
      : null;
    return { items, nextPageExists, total, statusCounts };
  };

  const handleCreate = () => {
    navigate('/customers/new');
  };

  const handleImportNavigate = () => {
    navigate('/customers/import');
  };

  const handleEdit = (customer: Customer) => {
    if (!canView) {
      return;
    }
    navigate(`/customers/${customer.id}/edit`);
  };

  const handleDelete = async (id: number) => {
    try {
      await customerService.delete(id);
      message.success('Customer deleted');
      fetchCustomers();
    } catch {
      message.error('Failed to delete customer');
    }
  };

  const handleBatchDelete = async () => {
    if (!selectAll && selectedRowKeys.length === 0) {
      return;
    }
    setDeleting(true);
    try {
      const filters = filter === 'all' ? {} : { type: filter };
      const payload = selectAll
        ? { select_all: true, excluded_ids: excludedRowKeys.map((key) => Number(key)), filters }
        : { ids: selectedRowKeys.map((key) => Number(key)), filters };
      await customerService.batchDelete(payload);
      message.success('Customers deleted');
      setSelectedRowKeys([]);
      setSelectAll(false);
      setExcludedRowKeys([]);
      fetchCustomers();
    } catch {
      message.error('Failed to delete customers');
    } finally {
      setDeleting(false);
    }
  };

  const enabledCustomers = useMemo(
    () => customers.filter((customer) => (customer as any).enabled !== -1),
    [customers]
  );

  const filteredCustomers = useMemo(() => {
    if (filter === 'all') {
      return enabledCustomers;
    } else if (filter === 'potential') {
      return enabledCustomers.filter(customer => customer.type === 'potential');
    } else if (filter === 'active') {
      return enabledCustomers.filter(customer => customer.type === 'active' || (customer as any).type === 'existing');
    } else if (filter === 'inactive') {
      return enabledCustomers.filter(customer => customer.type === 'inactive' || (customer as any).type === 'resigned');
    }
    return enabledCustomers;
  }, [enabledCustomers, filter]);

  const baseColumns = useMemo(() => {
    const columns = [
    { 
      title: 'Customer', 
      dataIndex: 'name', 
      key: 'name', 
      width: 180,
      sorter: (a: Customer, b: Customer) => (a.name || '').localeCompare(b.name || ''),
      render: (name: string) => (
        <Tooltip title={name || '-'} placement="topLeft">
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name || '-'}
          </div>
        </Tooltip>
      ),
    },
    { 
      title: 'Identification No.', 
      dataIndex: 'ic_passport_no', 
      key: 'ic_passport_no', 
      width: 160,
      sorter: (a: Customer, b: Customer) => (a.ic_passport_no || '').localeCompare(b.ic_passport_no || ''),
      render: (value: string) => {
        const displayValue = value || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    { 
      title: 'Tin No.', 
      dataIndex: 'tin_no', 
      key: 'tin_no', 
      width: 120,
      sorter: (a: Customer, b: Customer) => (a.tin_no || '').localeCompare(b.tin_no || ''),
      render: (value: string) => {
        const displayValue = value || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    { 
      title: 'Nationality', 
      dataIndex: 'nationality', 
      key: 'nationality', 
      width: 120,
      sorter: (a: Customer, b: Customer) => (a.nationality || '').localeCompare(b.nationality || ''),
      render: (value: string) => {
        const displayValue = value || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    { 
      title: 'Race', 
      dataIndex: 'race', 
      key: 'race', 
      width: 100,
      sorter: (a: Customer, b: Customer) => (a.race || '').localeCompare(b.race || ''),
      render: (value: string) => {
        const displayValue = value || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    { 
      title: 'Sex', 
      dataIndex: 'sex', 
      key: 'sex', 
      width: 90,
      sorter: (a: Customer, b: Customer) => (a.sex || '').localeCompare(b.sex || ''),
      render: (value: string) => {
        const displayValue = value || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status Label',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      visible: false,
      sorter: (a: Customer, b: Customer) => (a.type || '').localeCompare(b.type || ''),
      render: (value: string) => {
        const displayValue = value || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Contact No.',
      dataIndex: 'contact_number',
      key: 'contact_number',
      width: 140,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        ((a.contacts?.[0]?.contact_number || '') as string).localeCompare(b.contacts?.[0]?.contact_number || ''),
      render: (_: string, record: Customer) => {
        const displayValue = record.contacts?.[0]?.contact_number || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        ((a.email || a.contacts?.[0]?.email || '') as string).localeCompare(b.email || b.contacts?.[0]?.email || ''),
      render: (_: string, record: Customer) => {
        const displayValue = record.email || record.contacts?.[0]?.email || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Address',
      dataIndex: 'address1',
      key: 'address1',
      width: 200,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        ((a.contacts?.[0]?.address1 || '') as string).localeCompare(b.contacts?.[0]?.address1 || ''),
      render: (_: string, record: Customer) => {
        const displayValue = record.contacts?.[0]?.address1 || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Assessment',
      dataIndex: 'assessment',
      key: 'assessment',
      width: 200,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        ((a.assessment || '') as string).localeCompare(b.assessment || ''),
      render: (_: string, record: Customer) => {
        const displayValue = record.assessment || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Bank',
      dataIndex: 'bank',
      key: 'bank',
      width: 200,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        ((a.reporting?.bank_text || '') as string).localeCompare(b.reporting?.bank_text || ''),
      render: (_: string, record: Customer) => {
        const displayValue = record.reporting?.bank_text || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Advisor',
      dataIndex: 'agent_staff_id',
      key: 'agent_staff_id',
      width: 160,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        (a.advisor_staff?.name || '').localeCompare(b.advisor_staff?.name || ''),
      render: (_: number, record: Customer) => {
        const displayValue = record.advisor_staff?.name || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Advisor Appoint',
      dataIndex: 'agent_appoint_date',
      key: 'agent_appoint_date',
      width: 180,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        (a.agent_appoint_date || '').localeCompare(b.agent_appoint_date || ''),
      render: (value: string) => {
        const displayValue = value ? dayjs(value).format('DD/MM/YYYY') : '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Advisor Resign',
      dataIndex: 'agent_resign_date',
      key: 'agent_resign_date',
      width: 180,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        (a.agent_resign_date || '').localeCompare(b.agent_resign_date || ''),
      render: (value: string) => {
        const displayValue = value ? dayjs(value).format('DD/MM/YYYY') : '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Tax Agent',
      dataIndex: 'staff_id',
      key: 'staff_id',
      width: 160,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        (a.tax_staff?.name || '').localeCompare(b.tax_staff?.name || ''),
      render: (_: number, record: Customer) => {
        const displayValue = record.tax_staff?.name || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Tax Agent Appoint',
      dataIndex: 'appoint_date',
      key: 'appoint_date',
      width: 180,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        (a.appoint_date || '').localeCompare(b.appoint_date || ''),
      render: (value: string) => {
        const displayValue = value ? dayjs(value).format('DD/MM/YYYY') : '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Tax Agent Resign',
      dataIndex: 'resign_date',
      key: 'resign_date',
      width: 180,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        (a.resign_date || '').localeCompare(b.resign_date || ''),
      render: (value: string) => {
        const displayValue = value ? dayjs(value).format('DD/MM/YYYY') : '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'MyTax Password',
      dataIndex: 'lhdn_password',
      key: 'lhdn_password',
      width: 160,
      visible: false,
      render: (_: string, record: Customer) => (record.lhdn_password ? 'Saved' : '-'),
    },
    {
      title: 'Spouse',
      dataIndex: 'spouse',
      key: 'spouse',
      width: 120,
      visible: false,
      render: (_: string, record: Customer) => record.family?.filter((member) => member.type === 'spouse').length ?? 0,
    },
    {
      title: 'Child',
      dataIndex: 'child',
      key: 'child',
      width: 120,
      visible: false,
      render: (_: string, record: Customer) => record.family?.filter((member) => member.type === 'children').length ?? 0,
    },
    {
      title: 'Signature',
      dataIndex: 'signature',
      key: 'signature',
      width: 140,
      visible: false,
      render: (_: string, record: Customer) => (
        <SignatureThumb customerId={record.id} signature={record.reporting?.signature} />
      ),
    },
    {
      title: 'Directors',
      dataIndex: 'directors',
      key: 'directors',
      width: 120,
      visible: false,
      render: (_: string, record: Customer) => {
        const directors = (record as any).directors || [];
        console.log('[Customers] Directors raw:', {
          customerId: record.id,
          directors,
        });
        return directors.length ? String(directors.length) : '-';
      },
    },
    {
      title: 'Members',
      dataIndex: 'members',
      key: 'members',
      width: 120,
      visible: false,
      render: (_: string, record: Customer) => {
        const members = (record as any).members || [];
        console.log('[Customers] Members raw:', {
          customerId: record.id,
          members,
        });
        return members.length ? String(members.length) : '-';
      },
    },
    {
      title: 'Instalment',
      dataIndex: 'installment',
      key: 'installment',
      width: 140,
      visible: false,
      render: (_: string, record: Customer) => {
        const installment = record.assets?.[0]?.installment;
        return typeof installment === 'number' ? installment.toFixed(2) : installment || '-';
      },
    },
    {
      title: 'Request',
      dataIndex: 'pending_requests',
      key: 'pending_requests',
      width: 110,
      render: (_: unknown, record: Customer) => {
        const pendingCount =
          (record.pending_customer_requests_count || 0) +
          (record.pending_contact_requests_count || 0) +
          (record.pending_financial_requests_count || 0) +
          (record.pending_reporting_requests_count || 0) +
          (record.pending_change_requests_count || 0);
        const hasPending = pendingCount > 0;
        return <Tag color={hasPending ? 'green' : 'default'}>{hasPending ? 'Yes' : 'No'}</Tag>;
      },
    },
    ];

    return columns.map((col) => {
      const key = String(col.key ?? '');
      if (numericSearchKeys.has(key)) {
        return { ...col, searchOperatorType: 'number' };
      }
      if (wordSearchKeys.has(key)) {
        return { ...col, searchOperatorType: 'word' };
      }
      return col;
    });
  }, [numericSearchKeys, wordSearchKeys]);

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
    setColumnConfig,
  } = useColumnVisibility(baseColumns);
  const maxVisibleColumns = 10;
  const { persistVisibleKeys } = useListingColumnsPersistence(
    listingKey,
    baseColumns,
    maxVisibleColumns,
    setColumnConfig
  );
  const { persistColumnWidths } = useListingColumnWidthsPersistence(
    listingKey,
    baseColumns,
    setColumnWidthByKey
  );
  const { persistSort } = useListingSortPersistence(
    listingKey,
    baseColumns,
    setSortKey,
    setSortOrder
  );
  const columnGroups = useMemo(
    () => [
      {
        title: 'Details',
        keys: [
          'name',
          'ic_passport_no',
          'nationality',
          'sex',
          'race',
          'assessment',
          'tin_no',
          'contact_number',
          'email',
          'address1',
          'bank',
          'lhdn_password',
          'pending_requests',
        ],
      },
      {
        title: 'Family',
        keys: ['spouse', 'child'],
      },
      {
        title: 'Signature',
        keys: ['signature'],
      },
      {
        title: 'Engagement',
        keys: [
          'agent_staff_id',
          'agent_appoint_date',
          'agent_resign_date',
          'staff_id',
          'appoint_date',
          'resign_date',
        ],
      },
      {
        title: 'Business',
        keys: ['directors', 'members'],
      },
      {
        title: 'Assets',
        keys: ['installment'],
      },
    ],
    []
  );
  const defaultVisibleKeys = useMemo(
    () => baseColumns.filter((col) => !('visible' in col) || (col as { visible?: boolean }).visible !== false).map((col) => col.key),
    [baseColumns]
  );
  const handleColumnVisibilityChangeLimited = useCallback(
    (keys: React.Key[]) => {
      if (keys.length > maxVisibleColumns) {
        message.warning(`You can select up to ${maxVisibleColumns} columns.`);
        return;
      }
      handleColumnVisibilityChange(keys);
      persistVisibleKeys(keys);
    },
    [handleColumnVisibilityChange, maxVisibleColumns, persistVisibleKeys]
  );
  const columnSelectionLocked = pendingVisibleKeys.length >= maxVisibleColumns;
  const filteredColumnConfig = useMemo(() => {
    if (!columnsSearch.trim()) {
      return columnConfig;
    }
    const query = columnsSearch.toLowerCase();
    return columnConfig.filter((col) => col.title.toLowerCase().includes(query));
  }, [columnConfig, columnsSearch]);
  const columnConfigByKey = useMemo(
    () => new Map(columnConfig.map((col) => [col.key, col])),
    [columnConfig]
  );

  const getSearchValueForColumn = useCallback((col: any) => {
    if (col.dataIndex === 'name') return (record: Customer) => record.name;
    if (col.dataIndex === 'ic_passport_no') return (record: Customer) => record.ic_passport_no;
    if (col.dataIndex === 'tin_no') return (record: Customer) => record.tin_no;
    if (col.dataIndex === 'nationality') return (record: Customer) => record.nationality;
    if (col.dataIndex === 'race') return (record: Customer) => record.race;
    if (col.dataIndex === 'sex') return (record: Customer) => record.sex;
    if (col.dataIndex === 'type') return (record: Customer) => record.type;
    if (col.dataIndex === 'contact_number') return (record: Customer) => record.contacts?.[0]?.contact_number;
    if (col.dataIndex === 'email') return (record: Customer) => record.email || record.contacts?.[0]?.email;
    if (col.dataIndex === 'address1') return (record: Customer) => record.contacts?.[0]?.address1;
    if (col.dataIndex === 'assessment') return (record: Customer) => record.assessment;
    if (col.dataIndex === 'bank') return (record: Customer) => record.reporting?.bank_text;
    if (col.dataIndex === 'agent_staff_id') return (record: Customer) => record.advisor_staff?.name;
    if (col.dataIndex === 'agent_appoint_date') return (record: Customer) => formatDateValue(record.agent_appoint_date);
    if (col.dataIndex === 'agent_resign_date') return (record: Customer) => formatDateValue(record.agent_resign_date);
    if (col.dataIndex === 'staff_id') return (record: Customer) => record.tax_staff?.name;
    if (col.dataIndex === 'appoint_date') return (record: Customer) => formatDateValue(record.appoint_date);
    if (col.dataIndex === 'resign_date') return (record: Customer) => formatDateValue(record.resign_date);
    if (col.dataIndex === 'lhdn_password') return (record: Customer) => (record.lhdn_password ? 'Saved' : '');
    if (col.dataIndex === 'spouse') return (record: Customer) => String(record.family?.filter((member) => member.type === 'spouse').length ?? 0);
    if (col.dataIndex === 'child') return (record: Customer) => String(record.family?.filter((member) => member.type === 'children').length ?? 0);
    if (col.dataIndex === 'signature') return (record: Customer) => (record.reporting?.signature ? 'Uploaded' : '');
    if (col.dataIndex === 'directors') return (record: Customer) => String((record as any).directors?.length ?? 0);
    if (col.dataIndex === 'members') return (record: Customer) => String((record as any).members?.length ?? 0);
    if (col.dataIndex === 'installment') return (record: Customer) => String(record.assets?.[0]?.installment ?? '');
    if (col.dataIndex === 'pending_requests') return (record: Customer) => {
      const pendingCount =
        (record.pending_customer_requests_count || 0) +
        (record.pending_contact_requests_count || 0) +
        (record.pending_financial_requests_count || 0) +
        (record.pending_reporting_requests_count || 0) +
        (record.pending_change_requests_count || 0);
      return pendingCount > 0 ? 'Yes' : 'No';
    };
    const key = String(col.key || '').toLowerCase();
    if (typeof col.dataIndex === 'string') {
      const dataIndex = col.dataIndex.toLowerCase();
      if (dataIndex.includes('date') || dataIndex.includes('due') || dataIndex.includes('appoint') || dataIndex.includes('resign')) {
        return (record: Customer) => formatDateValue((record as any)[col.dataIndex]);
      }
    }
    if (key.includes('date') || key.includes('due') || key.includes('appoint') || key.includes('resign')) {
      return (record: Customer) => formatDateValue((record as any)[col.key]);
    }
    return undefined as unknown as (record: Customer) => string | number | null | undefined;
  }, []);

  const searchColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        getSearchValue: getSearchValueForColumn(col),
      })),
    [visibleColumns, getSearchValueForColumn]
  );

  const memoizedColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        width: col.width,
        getSearchValue: getSearchValueForColumn(col),
      })),
    [visibleColumns, getSearchValueForColumn]
  );

  const { columnsWithSearch, filteredData, searchRowCells } = useTableColumnSearch<Customer>(
    filteredCustomers,
    memoizedColumns as any,
    searchColumns as any,
    { splitHeader: true, listingKey }
  );

  const sortedData = useMemo(() => {
    if (!sortKey || !sortOrder) {
      return filteredData;
    }
    const column = columnsWithSearch.find((col: any) => String(col.key) === sortKey);
    const sorter = column?.sorter;
    if (typeof sorter !== 'function') {
      return filteredData;
    }
    const sorted = [...filteredData].sort(sorter);
    return sortOrder === 'descend' ? sorted.reverse() : sorted;
  }, [filteredData, columnsWithSearch, sortKey, sortOrder]);

  const rowHeight = 44;
  const { focusedRowId, handleTableKeyDown, handleRowClick } = useListingTableFocus<Customer>({
    rows: sortedData,
    getRowId: (row) => row.id,
    containerRef: tableScrollRef,
    rowHeight,
  });

  const overscan = 6;
  const totalRows = sortedData.length;
  const visibleRange = useMemo(() => {
    if (!viewportHeight || totalRows === 0) {
      return { start: 0, end: totalRows, topPad: 0, bottomPad: 0 };
    }
    const maxStart = Math.max(0, totalRows - 1);
    const rawStart = Math.min(
      maxStart,
      Math.max(0, Math.floor(scrollTop / rowHeight) - overscan)
    );
    const rawEnd = Math.min(
      totalRows,
      Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan
    );
    const start = Math.min(rawStart, totalRows);
    const end = Math.max(start, rawEnd);
    const topPad = start * rowHeight;
    const bottomPad = Math.max(0, (totalRows - end) * rowHeight);
    return { start, end, topPad, bottomPad };
  }, [overscan, rowHeight, scrollTop, totalRows, viewportHeight]);
  const visibleRows = useMemo(
    () => sortedData.slice(visibleRange.start, visibleRange.end),
    [sortedData, visibleRange]
  );

  const handleSort = (key: string, sorter?: unknown) => {
    if (typeof sorter !== 'function') {
      return;
    }
    if (sortKey !== key) {
      setSortKey(key);
      setSortOrder('ascend');
      persistSort(key, 'ascend');
      return;
    }
    if (sortOrder === 'ascend') {
      setSortOrder('descend');
      persistSort(key, 'descend');
      return;
    }
    setSortKey(null);
    setSortOrder(null);
    persistSort(null, null);
  };

  const getCellValue = (record: Customer, col: any) => {
    if (!col.dataIndex) {
      return undefined;
    }
    if (Array.isArray(col.dataIndex)) {
      return col.dataIndex.reduce((acc: any, key: string) => (acc == null ? undefined : acc[key]), record);
    }
    return (record as any)[col.dataIndex];
  };

  const tableColumns = columnsWithSearch as any[];
  const totalValue = filteredData.length;
  const totalLabel = `Total: ${totalValue} ${totalValue === 1 ? 'customer' : 'customers'}`;
  const totalColumnCount = tableColumns.length + 1;
  const minColumnWidth = 50;
  const baseColumnWidths = useMemo(
    () =>
      tableColumns.map((col) => {
        const key = String(col.key);
        const cached = columnWidthByKey[key];
        if (typeof cached === 'number') {
          return cached;
        }
        if (typeof col.width === 'number') {
          return col.width;
        }
        if (typeof col.width === 'string') {
          const parsed = Number.parseFloat(col.width);
          if (Number.isFinite(parsed)) {
            return parsed;
          }
        }
        return 150;
      }),
    [tableColumns, columnWidthByKey]
  );
  const columnWidths = useMemo(() => {
    if (availableWidth == null) {
      return baseColumnWidths;
    }
    const total = baseColumnWidths.reduce((sum, width) => sum + width, 0) || 1;
    if (total <= availableWidth) {
      return baseColumnWidths;
    }
    const scale = availableWidth / total;
    return baseColumnWidths.map((width) => Math.max(minColumnWidth, Math.floor(width * scale)));
  }, [baseColumnWidths, availableWidth]);
  const handleResize = useCallback(
    (targetIndex: number) => (_e: unknown, data: { size: { width: number } }) => {
      const keys = tableColumns.map((col) => String(col.key));
      const current = columnWidths.map((width) => Math.max(minColumnWidth, Math.round(width)));
      const totalAvailable = availableWidth ?? current.reduce((sum, width) => sum + width, 0);
      const rightIndexes = current.map((_width, index) => index).filter((index) => index > targetIndex);
      if (rightIndexes.length === 0) {
        return;
      }
      const rightReducible = rightIndexes.reduce(
        (sum, index) => sum + Math.max(0, current[index] - minColumnWidth),
        0
      );
      const maxTarget = Math.max(minColumnWidth, current[targetIndex] + rightReducible);
      const nextWidth = Math.min(Math.max(Math.round(data.size.width), minColumnWidth), maxTarget);
      const delta = nextWidth - current[targetIndex];
      if (delta === 0) {
        return;
      }

      const updated = [...current];
      updated[targetIndex] = nextWidth;

      if (delta > 0) {
        const reducible = rightIndexes.map((index) => Math.max(0, current[index] - minColumnWidth));
        let remaining = delta;
        const totalReducible = reducible.reduce((sum, value) => sum + value, 0);
        if (totalReducible === 0) {
          return;
        }
        rightIndexes.forEach((index, position) => {
          const room = reducible[position];
          if (room === 0) return;
          const take = Math.min(room, Math.round((delta * room) / totalReducible));
          updated[index] = current[index] - take;
          remaining -= take;
        });
        if (remaining > 0) {
          for (let idx = 0; idx < rightIndexes.length && remaining > 0; idx += 1) {
            const index = rightIndexes[idx];
            if (updated[index] > minColumnWidth) {
              updated[index] -= 1;
              remaining -= 1;
            }
          }
        }
      } else {
        const add = -delta;
        const totalRight = rightIndexes.reduce((sum, index) => sum + current[index], 0);
        let remaining = add;
        rightIndexes.forEach((index) => {
          const give = Math.round((add * current[index]) / (totalRight || 1));
          updated[index] = current[index] + give;
          remaining -= give;
        });
        if (remaining !== 0) {
          for (let idx = 0; idx < rightIndexes.length && remaining !== 0; idx += 1) {
            const index = rightIndexes[idx];
            updated[index] += remaining > 0 ? 1 : -1;
            remaining += remaining > 0 ? -1 : 1;
          }
        }
      }

      const nextWidths: Record<string, number> = {};
      keys.forEach((key, index) => {
        nextWidths[key] = updated[index];
      });
      setColumnWidthByKey((prev) => {
        const merged = { ...prev, ...nextWidths };
        persistColumnWidths(merged);
        return merged;
      });
    },
    [availableWidth, columnWidths, minColumnWidth, persistColumnWidths, tableColumns]
  );

  useEffect(() => {
    const updateAvailableWidth = () => {
      const containerWidth = tableScrollRef.current?.clientWidth ?? 0;
      const borderAdjust = Math.max(totalColumnCount - 1, 0);
      const nextAvailable = Math.max(containerWidth - 48 - borderAdjust, 0);
      setAvailableWidth(nextAvailable || null);
    };

    updateAvailableWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateAvailableWidth);
      return () => window.removeEventListener('resize', updateAvailableWidth);
    }
    const observer = new ResizeObserver(updateAvailableWidth);
    if (tableScrollRef.current) {
      observer.observe(tableScrollRef.current);
    }
    return () => observer.disconnect();
  }, [totalColumnCount]);

  useEffect(() => {
    const container = tableScrollRef.current;
    if (!container) {
      return;
    }
    const updateHeight = () => {
      const rect = container.getBoundingClientRect();
      const nextHeight = Math.max(window.innerHeight - rect.top - 24, 200);
      setViewportHeight(nextHeight);
      const nextScrollbarWidth = Math.max(container.offsetWidth - container.clientWidth, 0);
      setScrollbarWidth(nextScrollbarWidth);
    };
    updateHeight();
    const mutationObserver = new MutationObserver(() => {
      updateHeight();
    });
    mutationObserver.observe(container, { childList: true, subtree: true, attributes: true });
    const handleScroll = () => {
      setScrollTop(container.scrollTop);
    };
    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateHeight);
    return () => {
      mutationObserver.disconnect();
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateHeight);
    };
  }, [filteredData.length]);

  const handleBatchExport = async () => {
    if (!selectAll && selectedRowKeys.length === 0) {
      return;
    }
    try {
      const filters = filter === 'all' ? {} : { type: filter };
      const payload = selectAll
        ? { select_all: true, excluded_ids: excludedRowKeys.map((key) => Number(key)), filters }
        : { ids: selectedRowKeys.map((key) => Number(key)), filters };
      const blob = await customerService.export(payload);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `personal-export-${dayjs().format('YYYYMMDD-HHmmss')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('Failed to export customers');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space size={4} wrap>
          <Button
            type={filter === 'all' ? 'primary' : 'default'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button
            type={filter === 'potential' ? 'primary' : 'default'}
            onClick={() => setFilter('potential')}
          >
            Potential ({statusCounts.potential})
          </Button>
          <Button
            type={filter === 'active' ? 'primary' : 'default'}
            onClick={() => setFilter('active')}
          >
            Active ({statusCounts.active})
          </Button>
          <Button
            type={filter === 'inactive' ? 'primary' : 'default'}
            onClick={() => setFilter('inactive')}
          >
            Inactive ({statusCounts.inactive})
          </Button>
        </Space>
        <Space>
            <Button
              icon={<ColumnHeightOutlined />}
              onClick={() => {
                setPendingVisibleKeys(visibleKeys);
                setColumnsSearch('');
                setColumnsModalOpen(true);
              }}
            >
              Columns
            </Button>
          {canDelete && (selectAll || selectedRowKeys.length > 0) && (
            <Popconfirm
              title={selectAll ? 'Delete all customers?' : `Delete ${selectedRowKeys.length} customer${selectedRowKeys.length === 1 ? '' : 's'}?`}
              okText="Delete"
              okButtonProps={{ danger: true, loading: deleting }}
              onConfirm={handleBatchDelete}
            >
              <Button danger loading={deleting}>
                Batch Delete
              </Button>
            </Popconfirm>
          )}
          {(selectAll || selectedRowKeys.length > 0) && (
            <Button
              icon={<span style={{ color: '#9254de' }}><DownloadOutlined /></span>}
              onClick={handleBatchExport}
                style={{
                  borderColor: '#9254de',
                  color: '#9254de',
                }}
              >
                Batch Export
              </Button>
            )}
            {canCreate && (
              <Button icon={<UploadOutlined />} onClick={handleImportNavigate}>
                Import
              </Button>
            )}
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              New Personal
            </Button>
          )}
        </Space>
      </div>
      <Modal
        title="Select Columns"
        open={columnsModalOpen}
        onCancel={() => setColumnsModalOpen(false)}
        width={820}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              type="link"
              onClick={() => {
                setPendingVisibleKeys(defaultVisibleKeys);
              }}
            >
              Reset to default
            </Button>
            <Space>
              <Button onClick={() => setColumnsModalOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                onClick={() => {
                  handleColumnVisibilityChangeLimited(pendingVisibleKeys);
                  setColumnsModalOpen(false);
                }}
              >
                Apply
              </Button>
            </Space>
          </div>
        )}
      >
        <Input
          placeholder="Search Columns"
          prefix={<SearchOutlined />}
          value={columnsSearch}
          onChange={(event) => setColumnsSearch(event.target.value)}
          style={{ marginBottom: 16 }}
        />
        {columnGroups.map((group) => {
          const groupColumns = group.keys
            .map((key) => columnConfigByKey.get(key))
            .filter((col): col is typeof columnConfig[number] => Boolean(col))
            .filter((col) => filteredColumnConfig.some((filtered) => filtered.key === col.key));

          if (groupColumns.length === 0) {
            return null;
          }

          return (
            <div key={group.title} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
                {group.title.toUpperCase()}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 12,
                }}
              >
                {groupColumns.map((col) => (
                  <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AntCheckbox
                      checked={pendingVisibleKeys.includes(col.key)}
                      disabled={columnSelectionLocked && !pendingVisibleKeys.includes(col.key)}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setPendingVisibleKeys((prev) => [...prev, col.key]);
                        } else {
                          setPendingVisibleKeys((prev) => prev.filter((key) => key !== col.key));
                        }
                      }}
                    />
                    <span style={{ color: columnSelectionLocked && !pendingVisibleKeys.includes(col.key) ? '#9ca3af' : undefined }}>
                      {col.title}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </Modal>
      {submitNotice && (
        <div style={{ marginBottom: 16 }}>
          <Alert color={submitNotice.type} onDismiss={() => setSubmitNotice(null)}>
            {submitNotice.message}
          </Alert>
        </div>
      )}

        <div className="flowbite-listing-table-shell">
          <div style={{ paddingRight: scrollbarWidth }}>
            <FlowbiteTable className="flowbite-listing-table header-only w-full table-fixed" hoverable>
              <colgroup>
                <col style={{ width: 48 }} />
                {tableColumns.map((col, index) => (
                  <col key={`head_${String(col.key)}`} style={{ width: columnWidths[index] }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                <TableHeadCell className="w-12">
                  <Checkbox
                    checked={selectAll || (sortedData.length > 0 && selectedRowKeys.length === sortedData.length)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (e.target.checked) {
                        setSelectAll(true);
                        setExcludedRowKeys([]);
                        setSelectedRowKeys([]);
                      } else {
                        setSelectAll(false);
                        setExcludedRowKeys([]);
                        setSelectedRowKeys([]);
                      }
                    }}
                  />
                </TableHeadCell>
                  {tableColumns.map((col, index) => {
                    const key = String(col.key);
                    const isSortable = typeof col.sorter === 'function';
                    const isSorted = sortKey === key;
                    const sortIndicator =
                      isSorted && sortOrder
                        ? sortOrder === 'ascend'
                          ? '▲'
                          : '▼'
                        : '';
                    const isFirstDataColumn = index === 0;
                    const maxWidth =
                      availableWidth != null
                        ? Math.max(minColumnWidth, availableWidth - minColumnWidth * (columnWidths.length - 1))
                        : undefined;

                    return (
                      <FlowbiteResizableHeader
                        key={key}
                        width={columnWidths[index]}
                        maxWidth={maxWidth}
                        onResize={handleResize(index)}
                        onClick={() => handleSort(key, col.sorter)}
                        className={isSortable ? 'cursor-pointer select-none' : undefined}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div>{col.title}</div>
                            {isFirstDataColumn && (
                              <div className="mt-1 text-xs text-gray-400">{totalLabel}</div>
                            )}
                          </div>
                          {isSortable && (
                            <span className="text-xs text-gray-400">{sortIndicator}</span>
                          )}
                        </div>
                      </FlowbiteResizableHeader>
                    );
                  })}
                </TableRow>
                <TableRow>
                  <TableHeadCell className="w-12" />
                  {tableColumns.map((col, index) => {
                    const key = String(col.key);
                    return (
                      <TableHeadCell key={`${key}-search`} className="py-1 px-2">
                        {searchRowCells?.[index] ?? null}
                      </TableHeadCell>
                    );
                  })}
                </TableRow>
              </TableHead>
            </FlowbiteTable>
          </div>
          <div
          className="flowbite-listing-table-scroll"
          ref={tableScrollRef}
          tabIndex={0}
          onKeyDown={handleTableKeyDown}
          style={
            viewportHeight
              ? {
                  height: viewportHeight,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }
              : { overflowY: 'auto', overflowX: 'hidden' }
          }
        >
            <FlowbiteTable className="flowbite-listing-table w-full table-fixed" hoverable>
              <colgroup>
                <col style={{ width: 48 }} />
                {tableColumns.map((col, index) => (
                  <col key={`body_${String(col.key)}`} style={{ width: columnWidths[index] }} />
                ))}
              </colgroup>
              <TableBody className="divide-y">
                {loading && (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length + 1} className="py-8 text-center text-sm text-gray-500">
                      Loading...
                    </TableCell>
                  </TableRow>
                )}
                {!loading && sortedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length + 1} className="py-8 text-center text-sm text-gray-500">
                      No customers
                    </TableCell>
                  </TableRow>
                )}
                {!loading && sortedData.length > 0 && (
                  <>
                    {visibleRange.topPad > 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={tableColumns.length + 1}
                          style={{ height: visibleRange.topPad, padding: 0 }}
                        />
                      </TableRow>
                    )}
                    {visibleRows.map((record: Customer, index: number) => {
                      const isSelected = selectedRowKeys.includes(record.id);
                      const isFocused = focusedRowId === record.id;
                      return (
                        <TableRow
                          key={record.id}
                          data-row-id={record.id}
                          className={`hover:bg-gray-50 ${isFocused ? 'bg-blue-50' : ''}`}
                          onDoubleClick={canView ? () => handleEdit(record) : undefined}
                          onClick={() => handleRowClick(record.id)}
                        >
                          <TableCell className="w-12">
                            <Checkbox
                              checked={selectAll ? !excludedRowKeys.includes(record.id) : isSelected}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => {
                                if (selectAll) {
                                  setExcludedRowKeys((prev) =>
                                    prev.includes(record.id)
                                      ? prev.filter((key) => key !== record.id)
                                      : [...prev, record.id]
                                  );
                                  return;
                                }
                                setSelectedRowKeys((prev) =>
                                  prev.includes(record.id)
                                    ? prev.filter((key) => key !== record.id)
                                    : [...prev, record.id]
                                );
                              }}
                            />
                          </TableCell>
                          {tableColumns.map((col, index) => {
                            const value = getCellValue(record, col);
                            const content = typeof col.render === 'function'
                              ? col.render(value, record, index)
                              : value ?? '-';
                            return (
                              <TableCell key={`${record.id}_${String(col.key)}`} style={{ width: columnWidths[index] }}>
                                {content}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                    {visibleRange.bottomPad > 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={tableColumns.length + 1}
                          style={{ height: visibleRange.bottomPad, padding: 0 }}
                        />
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </FlowbiteTable>
          </div>
      </div>
    </div>
  );
};
